import React, { Component } from 'react';
import { Modal as ModalModule, Utils } from 'common';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';

import UploadContacts from './UploadContacts.component';
import SaveOrSelectTemplate from './SaveOrSelectTemplate.component';
import PageNames from '../utils/PageNames.enum';
import AssignGroups from './AssignGroups.component';
import MapContactFields from './MapContactFields.component';
import DuplicatesAndErrors from './DuplicatesAndErrors.component';
import ContactsCommunication from '../utils/ContactsCommunication';
import MapContactFieldsUtil from '../utils/MapContactFieldsUtil';
import Template from '../utils/Template';

const { Modal, ModalButtonActionType } = ModalModule;
const { withLayoutAndModalContexts } = Utils;

class ImportContactsModal extends Component {
	constructor(props) {
		super(props);

		const {
			data: { duplicatesAndErrors, openPage },
		} = this.props;

		this.state = {
			currentPage: openPage || PageNames.UploadContacts,
			isFileUploadSuccessful: !!duplicatesAndErrors,
			isDuplicatesSaved: true,
			isTemplateSaved: false,
			isTemplateSaveSkipped: false,
			isTemplateMapped: false,
			savedTemplates: [],
			sdcMappedContactFields: {},
			importedContactsFileData: null,
			isSDCTemplateMatched: false,
			navigationStack: [PageNames.UploadContacts],
			selectedGroup: -1,
			isRowOneHeader: true,
			fileDataId: '',
			templateId: 0,
			importedData: duplicatesAndErrors
				? this.updateImportedData(duplicatesAndErrors)
				: this.buildDefaultImportedData(),
			mappedData: {},
			isContinueDisabled: false,
		};
	}

	componentDidMount() {
		const { currentPage } = this.state;
		if (currentPage === PageNames.UploadContacts) {
			this.getTemplates();
		}
	}

	updateState = states => {
		this.setState({ ...states });
	};

	getTemplates = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const response = await ContactsCommunication.getSavedTemplates();

		if (ContactsCommunication.isSuccessful(response)) {
			const savedTemplates = isArray(response.data)
				? response.data.sort((currentTemplate, nextTemplate) =>
						nextTemplate.TemplateName.localeCompare(currentTemplate.TemplateName)
				  )
				: [];

			this.setState({
				savedTemplates,
			});
		} else {
			showError({ response });
		}
	};

	buildDefaultImportedData = () => ({
		importedDataErrors: null,
		importedDataDuplicates: null,
		importedContactsRows: null,
	});

	buildMappedData = () => {
		const { importedData } = this.state;
		const mappedData = MapContactFieldsUtil.buildMappedData(
			importedData.importedContactsRows,
			0
		);

		this.updateState({
			mappedData,
			sdcMappedContactFields: mappedData.sdcColumnMapping,
			isContinueDisabled: mappedData.noOfMappedColumns === 0,
		});
	};

	updateImportedData = (uploadedData, importedContactsRows) => {
		const localImportData = this.buildDefaultImportedData();

		localImportData.importedContactsRows = importedContactsRows;
		localImportData.importedDataDuplicates = isArray(uploadedData?.DuplicateRows)
			? uploadedData.DuplicateRows
			: [];
		localImportData.importedDataErrors = isArray(uploadedData?.ErrorRows)
			? uploadedData.ErrorRows
			: [];

		return localImportData;
	};

	uploadContactsCallback = uploadedData => {
		const { First5Rows, FileDataId, TemplateId, importedFileName } = uploadedData || {};
		const isRowsExists = First5Rows && First5Rows.root && First5Rows.root.Co;

		this.setState({
			isSDCTemplateMatched: TemplateId === 1,
			importedContactsFileData: { importedFileName },
			isFileUploadSuccessful: true,
			fileDataId: FileDataId,
			importedData: this.updateImportedData(uploadedData, isRowsExists),
		});
	};

	mapContactsCallback = data => {
		const { isMapped, isFinalize, newSdcMappedContactFields } = data;
		const { sdcMappedContactFields } = this.state;

		this.setState(
			{
				isContinueDisabled: !isMapped,
				sdcMappedContactFields: !isMapped
					? MapContactFieldsUtil.buildDefaultMapping()
					: newSdcMappedContactFields || sdcMappedContactFields,
			},
			() => {
				if (isFinalize) {
					this.openNextPage();
				}
			}
		);
	};

	onImport = () => {
		const {
			isSDCTemplateMatched,
			isRowOneHeader,
			selectedGroup,
			fileDataId,
			templateId,
			isDuplicatesSaved,
		} = this.state;
		const {
			data: { importContacts, params: oldParams },
			modalContext: { closeModal },
			modalId,
		} = this.props;

		const params = {
			TemplateId: isSDCTemplateMatched || !templateId ? 1 : templateId,
			HasHeader: isRowOneHeader,
			FileDataId: fileDataId,
			DuplicateHandle: 0,
			ContinueWithErrors: false,
			GroupId: selectedGroup,
		};
		importContacts(
			oldParams
				? {
						...oldParams,
						ContinueWithErrors: true,
						DuplicateHandle: isDuplicatesSaved ? 1 : 3,
				  }
				: params
		);
		closeModal(modalId, ModalButtonActionType.Ok);
	};

	onSaveTemplate = (isSuccessful, data) => {
		if (!isSuccessful) {
			this.setState({ isTemplateSaved: false });
			return;
		}

		this.setState(
			{
				templateId: data?.TemplateId,
			},
			this.openNextPage
		);
	};

	onContinueButtonClick = () => {
		const { currentPage, isTemplateSaved, isTemplateMapped } = this.state;

		switch (currentPage) {
			case PageNames.SaveTemplates:
			case PageNames.SelectTemplates:
				this.setState({ isTemplateSaved: !isTemplateSaved });
				break;
			case PageNames.MapContactsFields:
				this.setState({ isTemplateMapped: !isTemplateMapped });
				break;
			default:
				this.openNextPage();
				break;
		}
	};

	// Save the sdcMappedContactFields as a temporary template and use it for import.
	// If the save operation fails, it will default to using the Stamps.com template.
	// If the import still fails, then an error will be displayed to the user.
	onTemplateSaveSkipButtonClick = async () => {
		const { sdcMappedContactFields, isTemplateSaveSkipped } = this.state;

		const requestPayload = Template({
			IsTemp: true,
			Fields: sdcMappedContactFields,
		});

		const response = await ContactsCommunication.saveTemplate(requestPayload);

		if (ContactsCommunication.isSuccessful(response)) {
			this.onSaveTemplate(true, response.data);
		} else {
			this.setState({ isTemplateSaveSkipped: !isTemplateSaveSkipped });
		}
	};

	openNextPage = () => {
		const {
			currentPage,
			isSDCTemplateMatched,
			savedTemplates,
			importedData,
			navigationStack,
			templateId,
		} = this.state;
		const { importedDataDuplicates, importedDataErrors } = importedData;
		let newPage = Object.entries(PageNames)[currentPage + 1][1];

		switch (currentPage) {
			case PageNames.UploadContacts:
				if (isSDCTemplateMatched) {
					newPage = PageNames.AssignGroups;
				} else {
					newPage =
						savedTemplates.length > 0
							? PageNames.SelectTemplates
							: PageNames.MapContactsFields;
				}
				break;
			case PageNames.CheckDuplicates:
				newPage =
					isArray(importedDataErrors) && importedDataErrors.length > 0
						? PageNames.CheckErrors
						: PageNames.CheckDuplicates;
				break;
			case PageNames.SelectTemplates:
				newPage = templateId > 1000 ? PageNames.AssignGroups : PageNames.MapContactsFields;
				break;
			case PageNames.AssignGroups:
				if (isArray(importedDataDuplicates) && importedDataDuplicates.length > 0) {
					newPage = PageNames.CheckDuplicates;
				} else if (isArray(importedDataErrors) && importedDataErrors.length > 0) {
					newPage = PageNames.CheckErrors;
				}
				break;
			default:
				break;
		}

		if (newPage === PageNames.MapContactsFields) {
			this.buildMappedData();
		}

		this.setState({
			navigationStack: [...navigationStack, newPage],
			currentPage: newPage,
		});
	};

	openPreviousPage = () => {
		const { currentPage, navigationStack } = this.state;

		if (currentPage === PageNames.UploadContacts) {
			this.setState({
				isFileUploadSuccessful: false,
				importedData: this.buildDefaultImportedData(),
			});

			return;
		}

		const tempNavigationStack = navigationStack;
		tempNavigationStack.pop();
		const newPage = tempNavigationStack[tempNavigationStack.length - 1];

		this.setState({
			navigationStack: tempNavigationStack,
			currentPage: newPage >= 0 ? newPage : currentPage,
		});
	};

	renderPage = () => {
		const {
			data: { groups, fetchContacts },
		} = this.props;
		const {
			isFileUploadSuccessful,
			importedContactsFileData,
			isTemplateSaved,
			isTemplateSaveSkipped,
			sdcMappedContactFields,
			savedTemplates,
			importedData,
			isTemplateMapped,
			currentPage,
			mappedData,
		} = this.state;

		switch (currentPage) {
			case PageNames.UploadContacts:
				return (
					<UploadContacts
						isFileUploadSuccessful={isFileUploadSuccessful}
						importedContactsFileData={importedContactsFileData}
						uploadContactsCallback={this.uploadContactsCallback}
					/>
				);
			case PageNames.SelectTemplates:
			case PageNames.SaveTemplates:
				return (
					<SaveOrSelectTemplate
						isSelectTemplatePage={currentPage === PageNames.SelectTemplates}
						isTemplateSaved={isTemplateSaved}
						isTemplateSaveSkipped={isTemplateSaveSkipped}
						mappedTemplateFields={sdcMappedContactFields}
						savedTemplates={savedTemplates}
						finishSaveTemplate={this.onSaveTemplate}
					/>
				);
			case PageNames.AssignGroups:
				return (
					<AssignGroups
						groups={groups}
						fetchContacts={fetchContacts}
						setSelectedGroups={this.updateState}
					/>
				);
			case PageNames.CheckDuplicates:
			case PageNames.CheckErrors:
				return (
					<DuplicatesAndErrors
						isDuplicatePage={currentPage === PageNames.CheckDuplicates}
						importedData={importedData}
						setIsDuplicatesSaved={this.updateState}
					/>
				);
			case PageNames.MapContactsFields:
				return (
					<MapContactFields
						importedContactsRows={importedData.importedContactsRows}
						isTemplateMapped={isTemplateMapped}
						mappedData={mappedData}
						onIsRowOneHeaderChanged={this.updateState}
						mapContactsCallback={this.mapContactsCallback}
					/>
				);
			default:
				break;
		}
		return null;
	};

	renderFooterPrimaryButton = () => {
		const {
			currentPage,
			isFileUploadSuccessful,
			isContinueDisabled,
			importedData,
		} = this.state;
		const { importedDataErrors } = importedData;

		if (currentPage === PageNames.UploadContacts && !isFileUploadSuccessful) {
			return null;
		} else if (
			currentPage === PageNames.AssignGroups ||
			(currentPage === PageNames.CheckDuplicates && isEmpty(importedDataErrors)) ||
			currentPage === PageNames.CheckErrors
		) {
			return {
				type: ModalButtonActionType.Ok,
				onClick: this.onImport,
				text: currentPage === PageNames.AssignGroups ? 'Import' : 'Complete Import',
			};
		}

		return {
			type: ModalButtonActionType.Ok,
			onClick: this.onContinueButtonClick,
			disabled: currentPage === PageNames.MapContactsFields && isContinueDisabled,
			text: 'Continue',
		};
	};

	renderFooterSecondaryButton = () => {
		const { currentPage, isFileUploadSuccessful } = this.state;

		if (currentPage === PageNames.UploadContacts) {
			return !isFileUploadSuccessful
				? null
				: {
						type: ModalButtonActionType.Cancel,
						onClick: this.openPreviousPage,
						text: 'Change File',
				  };
		} else if (
			currentPage === PageNames.AssignGroups ||
			currentPage === PageNames.CheckDuplicates ||
			currentPage === PageNames.CheckErrors
		) {
			return null;
		} else if (
			currentPage === PageNames.SelectTemplates ||
			currentPage === PageNames.SaveTemplates
		) {
			return {
				type: ModalButtonActionType.Cancel,
				onClick:
					currentPage === PageNames.SaveTemplates
						? this.onTemplateSaveSkipButtonClick
						: this.openNextPage,
				text: 'Skip',
			};
		}

		return {
			type: ModalButtonActionType.Cancel,
			onClick: this.openPreviousPage,
			text: 'Back',
		};
	};

	render() {
		const { modalId } = this.props;
		const { isFileUploadSuccessful, currentPage } = this.state;
		// Set the modal size based on content
		const modalSize = currentPage === PageNames.MapContactsFields ? 'sheet' : 'small';

		return (
			<Modal
				modalId={modalId}
				className="contacts import-contacts-modal"
				header="Import Contacts"
				primaryButton={this.renderFooterPrimaryButton()}
				secondaryButton={this.renderFooterSecondaryButton()}
				hideFooter={!isFileUploadSuccessful}
				size={modalSize}
			>
				<div className="body">{this.renderPage()}</div>
			</Modal>
		);
	}
}

export default withLayoutAndModalContexts(ImportContactsModal);
